import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {Link, useNavigate} from "react-router-dom";
import './SearchBar.css';
import {RiSearchLine} from 'react-icons/ri';
import {fetchSearchResultByQuery} from '../../api/endpoints';
import SearchResult from '../../interfaces/SearchResult';
import {formatTimeZonedDateToFullDate} from '../../utils/DateUtils/DateUtils';
import {getEventUrlParams, getPerformerUrlParams, getVenueUrlParams} from '../../utils/GlobalUtils';

import {useScreenSizeContext} from '../../providers/ScreenSizeProvider';
import {isRight} from "fp-ts/Either";
import FavoriteButton from '../FavoriteButton/FavoriteButton';

interface SearchMenuProps {
    results: SearchResult;
}

interface SearchBarPart {
    htmlPart: React.ReactElement;
    actuallyDisplay: boolean;
    score: number;
}

const SearchMenu: React.FC<SearchMenuProps> = ({ results }) => {
    const searchArrayWithScores: SearchBarPart[] = [
        {
            actuallyDisplay: results.featured.length > 0,
            score: results.featuredScore,
            htmlPart: <>
                        <div className='menu-title'>Featured</div>
                        {results.featured.map(feature => (
                            <Link
                                key={feature.tevoEventId}
                                to={`/event/${getEventUrlParams(feature)}`}
                                className="menu-item"
                            >
                                <div className='text-xs-bold'>{feature.name}</div>
                                <div className='text-xs-light'>{formatTimeZonedDateToFullDate(feature.occursAtLocal)}</div>
                            </Link>
                        ))}
                    </>
        },
        {
            actuallyDisplay: results.performers.length > 0,
            score: results.performersScore,
            htmlPart: <>
                        <div className='menu-title'>Performers</div>
                        {results.performers.map(performer => (
                            <div key={performer.tevoPerformerId} className="menu-item-row">
                                <Link
                                    to={`/performer/${getPerformerUrlParams(performer)}`}
                                    className="menu-item menu-item-link"
                                >
                                    <div className='text-xs-bold'>{performer.name}</div>
                                    <div className='text-xs-light'>{performer.category.name}</div>
                                </Link>
                                <FavoriteButton
                                    type="performer"
                                    tevoId={performer.tevoPerformerId}
                                    name={performer.name}
                                />
                            </div>
                        ))}
                    </>
        },
        {
            actuallyDisplay: results.venues.length > 0,
            score: results.venuesScore,
            htmlPart: <>
                        <div className='menu-title'>Venues</div>
                        {results.venues.map(venue => (
                            <div key={venue.tevoVenueId} className="menu-item-row">
                                <Link
                                    to={`/venue/${getVenueUrlParams(venue)}`}
                                    className="menu-item menu-item-link"
                                >
                                    <div className='text-xs-bold'>{venue.name}</div>
                                    <div className='text-xs-light'>{venue.location}</div>
                                </Link>
                                <FavoriteButton
                                    type="venue"
                                    tevoId={venue.tevoVenueId}
                                    name={venue.name}
                                />
                            </div>
                        ))}
                    </>
        },
        {
            actuallyDisplay: results.events.length > 0,
            score: results.eventsScore,
            htmlPart: <>
                        <div className='menu-title'>Events</div>
                        {results.events.map(event => (
                            <Link
                                key={event.tevoEventId}
                                to={`/event/${getEventUrlParams(event)}`}
                                className="menu-item"
                            >
                                <div className='text-xs-bold'>{event.name}</div>
                                <div className='text-xs-light'>{formatTimeZonedDateToFullDate(event.occursAtLocal)}</div>
                            </Link>
                        ))}
                    </>
        }
    ]

    const sortedSearchArrayWithScores = searchArrayWithScores.sort((a, b) => b.score - a.score);

    return (
        <div className="search-menu">
            {
                sortedSearchArrayWithScores.some(item => item.actuallyDisplay) ?

                sortedSearchArrayWithScores
                .filter(item => item.actuallyDisplay)
                .map(item => item.htmlPart)

                :

                <div className='text-xs-bold' style={{margin: "5px 10px"}}>No Search Results</div>
            }
        </div>
    )
};

interface SearchBarRef {
    triggerSearchBarFocus: () => void;
}

interface SearchBarProps {
    onMobileSearchPressed?: () => void;
    dropdown?: boolean;
}
const SearchBar = forwardRef<SearchBarRef, SearchBarProps>(
    ({onMobileSearchPressed = () => null, dropdown=false}, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState('');
    const lastSearchedRef = useRef('');
    const [isFocused, setIsFocused] = useState(false);
    const [results, setResults] = useState<SearchResult | null>(null);
    const [loading, setLoading] = useState(false);

    const searchRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const {isMobile} = useScreenSizeContext();

    useImperativeHandle(ref, () => ({
        triggerSearchBarFocus: () => {
            if (inputRef.current) {
                setIsFocused(true);
                inputRef.current.focus();
            }
        },
    }));


    useEffect(() => {
        const handleClickOutsideSearch = (e: MouseEvent) => {
            if (searchRef.current && e.target instanceof Node && !searchRef.current.contains(e.target)) {
                setIsFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutsideSearch);

        return () => {
            document.removeEventListener('mousedown', handleClickOutsideSearch);
        };
    }, []);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
        if (e.target.value.length == 1) {
            fetchResults(e.target.value)
        }
    };


    const handleFocus = () => {
        if (isMobile && !dropdown) {
            onMobileSearchPressed();
        } else {
            setIsFocused(true);
        }
    };


    const fetchResults = async (query: string) => {
        if (query === '') {
            setResults(null);
            return;
        }
        setLoading(true);
        lastSearchedRef.current = query;

        const resultResponseEither = await fetchSearchResultByQuery(query);
        if (isRight(resultResponseEither)) {
            const result = resultResponseEither.right;
            if (lastSearchedRef.current === result.query) {
                setResults(result)
            }
        } else {
            console.error(resultResponseEither.left)
        }
        setLoading(false);
    }


    const debounceFetchResults = useCallback(
        (() => {
            let timeoutId: NodeJS.Timeout;
            return (searchQuery: string) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    fetchResults(searchQuery);
                }, 300);
            };
        })(),
        []
    );

    useEffect(() => {
        debounceFetchResults(query);
    }, [query, debounceFetchResults]);


    const handleEnter = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const q = query.trim();
        if (!q) {
            return;
        }
        setIsFocused(false);
        navigate(`/search/${encodeURIComponent(q)}`);
    };

    return (
        <div ref={searchRef}>
            <form className={`search-form ${isFocused ? 'focused-form' : ''}`} onSubmit={handleEnter}>
                {!loading ?
                    <RiSearchLine className={`search-icon ${isFocused ? 'focused-icon' : ''}`} />
                    :
                    <div className={`search-loading-spinner ${isFocused ? 'focused-search-loading-spinner' : ''}`}></div>
                }
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    placeholder={isMobile ? "Performers, teams, venues..." : "Search performers, teams, venues, and more..."}
                    className={`search-input ${isFocused ? 'focused-input' : ''}`}
                />
            </form>
            {isFocused && results !== null && <SearchMenu results={results}/>}
        </div>
    )
    })

export default SearchBar;
